import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import { useTranslation } from 'react-i18next';

export default function PatientData() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const patient = location.state?.patient || {};
  const [formData, setFormData] = useState({
    nameArabic: patient.name || "",
    nameEnglish: patient.nameEnglish || "",
    mobile: patient.mobile || "",
    gender: patient.gender || "",
    email: patient.email || "",
    country: patient.country || "",
    nationality: patient.nationality || "",
    idNumber: patient.idNumber || "",
    age: patient.age || "",
    address: patient.address || "",
    addressEnglish: patient.addressEnglish || "",
    medicalHistory: patient.medicalHistory || "",
    medicalHistoryEnglish: patient.medicalHistoryEnglish || "",
    emergencyContact: patient.emergencyContact || "",
    emergencyContactName: patient.emergencyContactName || "",
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { token } = useAuth();
  const navigate = useNavigate();

  // Refs for the file inputs
  const imageRef = useRef(null);

  useEffect(() => {
    if (patient.id) {
      setFormData({
        nameArabic: patient.name || "",
        nameEnglish: patient.nameEnglish || "",
        mobile: patient.mobile || "",
        gender: patient.gender || "",
        email: patient.email || "",
        country: patient.country || "",
        age: patient.age || "",
        address: patient.address || "",
        addressEnglish: patient.addressEnglish || "",
        medicalHistory: patient.medicalHistory || "",
        medicalHistoryEnglish: patient.medicalHistoryEnglish || "",
        emergencyContact: patient.emergencyContact || "",
        emergencyContactName: patient.emergencyContactName || "",
      });
    }
  }, [patient]);

  useEffect(() => {
    const fetchPatientDetails = async (id) => {
      try {
        const response = await fetch(`https://naql.nozzm.com/api/show_patients/${id}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
            'lang': i18n.language,
          },
        });

        const result = await response.json();
        if (result.status) {
          const fetchedPatient = result.data;

                  setFormData({
          nameArabic: fetchedPatient.name || "",
          nameEnglish: fetchedPatient.nameEnglish || "",
          mobile: fetchedPatient.mobile || "",
          gender: fetchedPatient.gender || "",
          email: fetchedPatient.email || "",
          country: fetchedPatient.country || "",
          nationality: fetchedPatient.nationality || "",
          idNumber: fetchedPatient.idNumber || "",
          age: fetchedPatient.age || "",
          address: fetchedPatient.address || "",
          addressEnglish: fetchedPatient.addressEnglish || "",
          medicalHistory: fetchedPatient.medicalHistory || "",
          medicalHistoryEnglish: fetchedPatient.medicalHistoryEnglish || "",
          emergencyContact: fetchedPatient.emergencyContact || "",
          emergencyContactName: fetchedPatient.emergencyContactName || "",
        });
        } else {
          setError(result.message || "Failed to fetch patient details");
        }
      } catch (error) {
        setError("Failed to fetch patient details. Please try again.");
      }
    };

    if (patient.id) {
      fetchPatientDetails(patient.id);
    }
  }, [patient.id, token, i18n.language]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const formDataToSend = new FormData();

      // Append non-file fields
      formDataToSend.append("name_ar", formData.nameArabic || "");
      formDataToSend.append("name_en", formData.nameEnglish || "");
      formDataToSend.append("gender", formData.gender || "");
      formDataToSend.append("country", formData.country || "");
      formDataToSend.append("nationality", formData.nationality || "");
      formDataToSend.append("id_number", formData.idNumber || "");
      formDataToSend.append("age", formData.age || "");
      formDataToSend.append("address_ar", formData.address || "");
      formDataToSend.append("address_en", formData.addressEnglish || "");
      formDataToSend.append("medical_history_ar", formData.medicalHistory || "");
      formDataToSend.append("medical_history_en", formData.medicalHistoryEnglish || "");
      formDataToSend.append("emergency_contact", formData.emergencyContact || "");
      formDataToSend.append("emergency_contact_name", formData.emergencyContactName || "");
      formDataToSend.append("email", formData.email || "");
      formDataToSend.append("mobile", formData.mobile || "");

      // Append files if they are uploaded
      if (imageRef.current && imageRef.current.files[0]) {
        formDataToSend.append("image", imageRef.current.files[0]);
      } else if (patient.image) {
        formDataToSend.append("image", patient.image);
      }

      const endpoint = patient.id
        ? `https://naql.nozzm.com/api/update_patients/${patient.id}`
        : "https://naql.nozzm.com/api/add_patients";

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          lang: "en",
        },
        body: formDataToSend,
      });

      const result = await response.json();
      if (!response.ok) {
        setError(result.message || "Failed to add/update patient");
        setIsLoading(false);
        return;
      }

      // After successful submission, navigate back to the Patients component and pass a state
      navigate("/Patients", { state: { updated: true } });
    } catch (error) {
      setError(
        "Failed to fetch. Please check your network connection and try again."
      );
      setIsLoading(false);
    }
  };

  return (
    <div className="container PatientsProfile tables bg-white">
      <div className="tableTitle d-flex justify-content-between">
        <h3>بيانات المريض</h3>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="row settingForm justify-content-around">
          <div className="col-md-6">
            <label htmlFor="nameArabic">الاسم باللغة العربية:</label>
            <input
              type="text"
              className="form-control"
              name="nameArabic"
              value={formData.nameArabic}
              onChange={handleChange}
              required
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="nameEnglish">الاسم باللغة الانجليزية:</label>
            <input
              type="text"
              className="form-control"
              name="nameEnglish"
              value={formData.nameEnglish}
              onChange={handleChange}
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="mobile">رقم الجوال:</label>
            <input
              type="text"
              className="form-control"
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              required
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="gender">الجنس:</label>
            <select
              className="form-control"
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              required
            >
              <option value="">اختر الجنس</option>
              <option value="ذكر">ذكر</option>
              <option value="أنثى">أنثى</option>
            </select>
          </div>
          <div className="col-md-6">
            <label htmlFor="email">الايميل:</label>
            <input
              type="email"
              className="form-control"
              name="email"
              value={formData.email}
              onChange={handleChange}
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="country">الدولة:</label>
            <input
              type="text"
              className="form-control"
              name="country"
              value={formData.country}
              onChange={handleChange}
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="nationality">الجنسية:</label>
            <input
              type="text"
              className="form-control"
              name="nationality"
              value={formData.nationality}
              onChange={handleChange}
              placeholder="مثال: سعودي، مصري، أردني"
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="idNumber">رقم الهوية:</label>
            <input
              type="text"
              className="form-control"
              name="idNumber"
              value={formData.idNumber}
              onChange={handleChange}
              placeholder="أدخل رقم الهوية الوطنية"
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="age">العمر:</label>
            <input
              type="number"
              className="form-control"
              name="age"
              value={formData.age}
              onChange={handleChange}
              min="0"
              max="150"
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="emergencyContact">رقم الطوارئ:</label>
            <input
              type="text"
              className="form-control"
              name="emergencyContact"
              value={formData.emergencyContact}
              onChange={handleChange}
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="emergencyContactName">اسم جهة الطوارئ:</label>
            <input
              type="text"
              className="form-control"
              name="emergencyContactName"
              value={formData.emergencyContactName}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-12">
            <label htmlFor="address">العنوان باللغة العربية:</label>
            <textarea
              className="form-control"
              name="address"
              value={formData.address}
              onChange={handleChange}
              rows="3"
            />
          </div>
          <div className="col-md-12">
            <label htmlFor="addressEnglish">العنوان باللغة الانجليزية:</label>
            <textarea
              className="form-control"
              name="addressEnglish"
              value={formData.addressEnglish}
              onChange={handleChange}
              rows="3"
            />
          </div>

          <div className="col-md-12">
            <label htmlFor="image">صورة المريض:</label>
            {patient.image && (
              <div className="mb-3">
                <img
                  src={patient.image}
                  alt="Patient"
                  style={{ maxWidth: "100px", maxHeight: "100px" }}
                />
              </div>
            )}
            <input
              type="file"
              name="image"
              ref={imageRef}
              className="form-control"
            />
          </div>

          <div className="col-md-12">
            <label htmlFor="medicalHistory">التاريخ الطبي باللغة العربية:</label>
            <textarea
              className="form-control"
              name="medicalHistory"
              value={formData.medicalHistory}
              onChange={handleChange}
              rows="4"
              placeholder="أدخل التاريخ الطبي للمريض"
            />
          </div>
          <div className="col-md-12">
            <label htmlFor="medicalHistoryEnglish">التاريخ الطبي باللغة الانجليزية:</label>
            <textarea
              className="form-control"
              name="medicalHistoryEnglish"
              value={formData.medicalHistoryEnglish}
              onChange={handleChange}
              rows="4"
              placeholder="Enter patient medical history"
            />
          </div>

          <div className="BottomButtons">
            <button className="save" type="submit" disabled={isLoading}>
              <span>{isLoading ? "جاري الحفظ..." : "حفظ"}</span>
            </button>
            <button
              className="cancel"
              type="button"
              onClick={() => navigate("/Patients")}
            >
              <span> الغاء</span>
            </button>
          </div>
        </div>
        {error && <div className="alert alert-danger">{error}</div>}
      </form>
    </div>
  );
}
